import {
  fetchUserTimesheets,
  fetchTimesheets,
  startShift,
  endShift,
  pauseShift,
  EndShiftRequestPayload,
} from '@/lib/request/postTimesheets';
import { postTimesheetAmend } from '@/lib/request/postTimesheetAmend';
import type { Timesheet, Timesheets } from '@/lib/request/types/timesheet';
import { DisplayTiming } from '@/lib/request/types/timesheetSetup';
import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import { useKioskConfigStore } from '@/stores/kioskConfig';
import { useAnalyticsService } from '@/lib/util/analytics/useAnalyticsService';
import { useRosterStore } from '@/stores/rosters';
import { useAreaStore } from '@/stores/areas';
import { useUserStore } from '@/stores/user';
import { useEmployeeWithTimesheetsStore } from '@/stores/employeesWithTimesheets/employeesWithTimesheets';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import UserError from '@/lib/request/errors/userError';

const SECONDS_IN_MINUTE = 60;

export function toTimesheetsEmployeeMap(data: Timesheets): TimesheetStoreData {
  return data.reduce(
    (map: TimesheetStoreData, record: Timesheet) => ({
      ...map,
      [record.Employee]: [...(map[record.Employee] || []), record],
    }),
    {}
  );
}

export interface TimesheetStoreStatus {
  isStartingTimesheet: boolean;
  isStartingBreak: boolean;
  isEndingBreak: boolean;
  isEndingTimesheet: boolean;
  hasEndedBreak: boolean;
  isEditingTimesheet: boolean;
  isLoading: boolean;
}

export type TimesheetStoreData = {
  [employeeId: number]: Timesheets;
};

export interface TimesheetStoreErrors {
  loadingError: Error | null;
  startingShiftError: Error | null;
  startingBreakError: Error | null;
  endingBreakError: Error | null;
  endingShiftError: Error | null;
  editingShiftError: Error | null;
}

export interface TimesheetStore {
  data: TimesheetStoreData;
  status: TimesheetStoreStatus;
  errors: TimesheetStoreErrors;
}

export interface ShiftActionTrackingParams {
  areaID?: number;
  locationID?: number;
  rosterId?: number;
  breakType?: string;
  errorMessage?: string;
  isScheduledShift?: boolean;
  timesheetId?: number;
  hasShiftQuestion?: boolean;
}

export enum EventSources {
  SelectArea = 'Select area',
  StartShift = 'Start shift',
  StartBreak = 'Start break',
  EndBreak = 'End break',
  EndShift = 'End shift',
  EndShiftConfirmation = 'End shift confirmation',
  SelectBreakType = 'Select break type',
}

export enum BreakTypeKeys {
  'M' = 'Unpaid',
  'R' = 'Paid',
}

export enum BreakType {
  Unpaid = 'M',
  Paid = 'R',
}

export enum AnalyticsBreakType {
  Unpaid = 'UNPAID_BREAK',
  Paid = 'PAID_BREAK',
}

export enum BreakState {
  ScheduledDuration = 'Scheduled Duration',
  NotStart = 'Not Start',
  Finished = 'Finished',
  InProgress = 'In Progress',
}

function mapBreakTypes(breakType: string) {
  const mappedBreakTypes = {
    [BreakType.Unpaid]: AnalyticsBreakType.Unpaid,
    [BreakType.Paid]: AnalyticsBreakType.Paid,
  };

  return mappedBreakTypes[breakType as keyof typeof BreakTypeKeys];
}

export enum TimesheetErrorResponseStatus {
  InactiveTimesheet = 412,
}

const TimesheetErrorResponseMessageMap = {
  [TimesheetErrorResponseStatus.InactiveTimesheet]:
    'Your timesheet is no longer in progress so cannot be submitted.',
};

export const useTimesheetStore = defineStore({
  id: 'timesheet',
  state: (): TimesheetStore => ({
    data: [],

    status: {
      isStartingTimesheet: false,
      isStartingBreak: false,
      isEndingBreak: false,
      isEndingTimesheet: false,
      hasEndedBreak: false,
      isEditingTimesheet: false,
      isLoading: false,
    },
    errors: {
      loadingError: null,
      startingShiftError: null,
      startingBreakError: null,
      endingBreakError: null,
      endingShiftError: null,
      editingShiftError: null,
    },
  }),
  getters: {
    isLoading: (state) => {
      return !!(
        state.status.isLoading ||
        state.status.isStartingTimesheet ||
        state.status.isStartingBreak ||
        state.status.isEndingBreak ||
        state.status.isEndingTimesheet ||
        state.status.isEditingTimesheet
      );
    },
    isProcessingShiftAction: (state) => {
      return !!(
        state.status.isStartingTimesheet ||
        state.status.isStartingBreak ||
        state.status.isEndingBreak ||
        state.status.isEndingTimesheet ||
        state.status.isEditingTimesheet
      );
    },
    hasAnyErrors: (state) => {
      return !!(
        state.errors.loadingError ||
        state.errors.startingShiftError ||
        state.errors.startingBreakError ||
        state.errors.endingBreakError ||
        state.errors.endingShiftError ||
        state.errors.editingShiftError
      );
    },
    isOnShift(state) {
      const userStore = useUserStore();
      const userTimesheets = state.data[userStore.activeUserOrThrow.Id];

      if (!Array.isArray(userTimesheets)) {
        return false;
      }

      return !!userTimesheets.find((timesheet) => {
        return timesheet.IsInProgress;
      });
    },
    isOnBreak(state) {
      const userStore = useUserStore();

      return !!state.data[userStore.activeUserOrThrow.Id]
        .find((timesheet) => timesheet.IsInProgress)
        ?.Slots.find(({ strState }) => strState == BreakState.InProgress);
    },
    activeTimesheet(state) {
      const userStore = useUserStore();

      return state.data[userStore.activeUserOrThrow.Id]?.find(
        ({ IsInProgress }) => IsInProgress
      );
    },
    getTimesheetsByEmployeeId(state) {
      return (id: number) => state.data[id] || [];
    },
    getActiveUserTimesheetById(state) {
      const userStore = useUserStore();

      return (timesheetId: number) =>
        state.data[userStore.activeUserOrThrow.Id].find(
          (timesheet) => timesheet.Id === timesheetId
        );
    },
    // Returns the active timesheet, or an error if there's none
    // Only use this method when there's guarantee that an active timesheet exists
    activeTimesheetOrThrow(state): Timesheet {
      const userStore = useUserStore();

      const activeTimesheet = state.data[userStore.activeUserOrThrow.Id].find(
        ({ IsInProgress }) => IsInProgress
      );

      if (!activeTimesheet) {
        throw new Error('Theres no active timesheet');
      }

      return activeTimesheet;
    },
    getStartShiftTime() {
      return () => this.activeTimesheetOrThrow.StartTimeLocalized;
    },
    getLastFinishedTimesheetEndTime() {
      const userStore = useUserStore();

      return () =>
        this.getTimesheetsByEmployeeId(userStore.activeUserOrThrow.Id)[
          this.getTimesheetsByEmployeeId(userStore.activeUserOrThrow.Id)
            .length - 2
        ]?.EndTimeLocalized || null;
    },
    getInitialStartTime() {
      return () =>
        DateTime.fromISO(
          this.activeTimesheetOrThrow.StartTimeLocalized
        ).toMillis() || 0;
    },
    getWorkedElapsedTimeInMinutes() {
      const MILLISECONDS_IN_MINUTE = 60000;
      const initialStartTime: number = this.getInitialStartTime();
      const currentTime = DateTime.now().toMillis();

      return (endTime = currentTime, startTime = initialStartTime) =>
        Math.floor(
          (endTime - startTime) / MILLISECONDS_IN_MINUTE -
            this.getTimeOnUnpaidBreak()
        );
    },
    getTotalElapsedTimeInMinutes() {
      const MILLISECONDS_IN_MINUTE = 60000;
      const startTime =
        DateTime.fromISO(
          this.activeTimesheetOrThrow.StartTimeLocalized
        ).toMillis() || 0;
      const currentTime = DateTime.now().toMillis();

      return () =>
        Math.floor((currentTime - startTime) / MILLISECONDS_IN_MINUTE);
    },
    getTimeOnUnpaidBreak() {
      const timeOnUnpaidBreak =
        this.getBreaks()
          .filter(
            ({ mixedActivity, strState }) =>
              mixedActivity.strBreakType == BreakType.Unpaid &&
              strState == BreakState.Finished
          )
          .reduce(
            (total, slot) => slot.intUnixEnd - slot.intUnixStart + total,
            0
          ) / SECONDS_IN_MINUTE;

      return () => timeOnUnpaidBreak;
    },
    canEndShift() {
      const kioskConfigStore = useKioskConfigStore();
      const timesheetBlock = kioskConfigStore.timesheetClosestBlock;
      return () => this.getTotalElapsedTimeInMinutes() >= timesheetBlock;
    },
    getBreaks() {
      return () => this.activeTimesheetOrThrow.Slots || [];
    },
    getFinishedBreaks() {
      return () =>
        this.activeTimesheetOrThrow.Slots?.filter(
          ({ strState }) => strState == BreakState.Finished
        ) || [];
    },
    getAllScheduledBreaks() {
      const rosterStore = useRosterStore();
      return () =>
        this.getBreaks()
          .slice(0, rosterStore.getInvalidAndValidScheduledBreaksCount())
          .filter((slot) => slot.intUnixEnd);
    },
    getAllValidBreaks() {
      return () =>
        this.getBreaks().filter(
          ({ strState, intUnixEnd, intUnixStart }) =>
            (strState == BreakState.ScheduledDuration &&
              intUnixEnd - intUnixStart != 0) ||
            strState != BreakState.ScheduledDuration
        ) || [];
    },
    getUnfinishedPaidScheduledBreaks() {
      return () =>
        this.activeTimesheetOrThrow.Slots?.filter(
          ({ strState, mixedActivity, intUnixEnd, intUnixStart }) =>
            (strState == BreakState.NotStart ||
              strState == BreakState.ScheduledDuration) &&
            mixedActivity.strBreakType == BreakType.Paid &&
            intUnixEnd - intUnixStart != 0
        ) || [];
    },
    getUnfinishedUnpaidScheduledBreakId() {
      const activeBreakId = this.activeTimesheetOrThrow.Slots?.findIndex(
        ({ strState, mixedActivity, intUnixEnd, intUnixStart }) =>
          (strState == BreakState.NotStart ||
            strState == BreakState.ScheduledDuration) &&
          mixedActivity.strBreakType == BreakType.Unpaid &&
          intUnixEnd - intUnixStart != 0
      );

      return () => activeBreakId;
    },
    getUnfinishedUnpaidScheduledBreaks() {
      return () =>
        this.activeTimesheetOrThrow.Slots?.filter(
          ({ strState, mixedActivity, intUnixEnd, intUnixStart }) =>
            (strState == BreakState.NotStart ||
              strState == BreakState.ScheduledDuration) &&
            mixedActivity.strBreakType == BreakType.Unpaid &&
            intUnixEnd - intUnixStart != 0
        ) || [];
    },
    getStartBreakTime() {
      return (index: number) => this.getBreaks()[index]?.intUnixStart;
    },
    getEndBreakTime() {
      return (index: number) => this.getBreaks()[index]?.intUnixEnd;
    },
    getFinishedBreakTotalTime() {
      return (index: number) =>
        Math.floor(
          (this.getBreaks()[index].intUnixEnd -
            this.getBreaks()[index].intUnixStart) /
            SECONDS_IN_MINUTE
        );
    },
    getFinishedBreakStartTimeById() {
      return (breakId: number) =>
        this.activeTimesheetOrThrow.Slots[breakId]?.strState ==
        BreakState.Finished
          ? this.activeTimesheetOrThrow.Slots[breakId]?.intUnixStart
          : null;
    },
    getFinishedBreakEndTimeById() {
      return (breakId: number) =>
        this.activeTimesheetOrThrow.Slots[breakId]?.strState ==
        BreakState.Finished
          ? this.activeTimesheetOrThrow.Slots[breakId]?.intUnixEnd
          : null;
    },
    getActiveBreakInitialTime() {
      let startTime = 0;

      this.activeTimesheet?.Slots?.find(({ strState, intUnixStart }) => {
        if (strState == BreakState.InProgress) {
          startTime = intUnixStart;
        }
      });

      return () => Math.floor(startTime);
    },
    getActiveBreak() {
      return () =>
        this.activeTimesheetOrThrow.Slots?.find(
          ({ strState }) => strState === BreakState.InProgress
        );
    },
    getActiveBreakId() {
      const activeBreakId = this.activeTimesheetOrThrow.Slots?.findIndex(
        ({ strState }) => strState === BreakState.InProgress
      );

      return () => activeBreakId;
    },
    getActiveBreakName() {
      let activeBreakName = '';

      this.activeTimesheetOrThrow.Slots?.find(({ strState, strTypeName }) => {
        if (strState == BreakState.InProgress) {
          activeBreakName = strTypeName;
        }
      });

      if (!activeBreakName) {
        this.activeTimesheetOrThrow.Slots?.find(({ strState, strTypeName }) => {
          if (strState == BreakState.Finished) {
            activeBreakName = strTypeName;
          }
        });
      }

      return () => activeBreakName;
    },
    getActiveBreakType() {
      let activeBreakType = '';

      this.activeTimesheetOrThrow.Slots?.find(({ strState, mixedActivity }) => {
        if (strState == BreakState.InProgress) {
          activeBreakType =
            mixedActivity.strBreakType == BreakType.Unpaid
              ? '(unpaid)'
              : '(paid)';
        }
      });

      if (!activeBreakType) {
        this.activeTimesheetOrThrow.Slots?.find(
          ({ strState, mixedActivity }) => {
            if (strState == BreakState.Finished) {
              activeBreakType =
                mixedActivity.strBreakType == BreakType.Unpaid
                  ? '(unpaid)'
                  : '(paid)';
            }
          }
        );
      }

      return () => activeBreakType;
    },
    getScheduledBreakId() {
      return (breakType: string): number | undefined =>
        this.activeTimesheetOrThrow.Slots?.findIndex(
          ({ strState, mixedActivity, intUnixStart, intUnixEnd }) =>
            (strState === BreakState.ScheduledDuration ||
              strState === BreakState.NotStart) &&
            mixedActivity.strBreakType === breakType &&
            intUnixEnd - intUnixStart != 0
        );
    },
    getElapsedIntegerHours() {
      const currentTime = DateTime.now().toMillis();
      const initialStartTime: number = this.getInitialStartTime();

      return (endTime = currentTime, startTime = initialStartTime) =>
        this.getWorkedElapsedTimeInMinutes(endTime, startTime) < 60
          ? 0
          : Math.floor(
              this.getWorkedElapsedTimeInMinutes(endTime, startTime) / 60
            );
    },
    getElapsedRemainderMinutes() {
      const currentTime = DateTime.now().toMillis();
      const initialStartTime: number = this.getInitialStartTime();

      return (endTime = currentTime, startTime = initialStartTime) =>
        this.getWorkedElapsedTimeInMinutes(endTime, startTime) < 60
          ? Math.floor(this.getWorkedElapsedTimeInMinutes(endTime, startTime))
          : this.getWorkedElapsedTimeInMinutes(endTime, startTime) % 60;
    },
    getElapsedIntegerHoursOnBreak() {
      const timeInMinutes =
        (DateTime.now().toSeconds() - this.getActiveBreakInitialTime()) /
        SECONDS_IN_MINUTE;

      return () => (timeInMinutes < 60 ? 0 : Math.floor(timeInMinutes / 60));
    },
    getElapsedRemainderMinutesOnBreak() {
      const currentTime = DateTime.now().toSeconds() / SECONDS_IN_MINUTE;
      const breakStartTime =
        this.getActiveBreakInitialTime() / SECONDS_IN_MINUTE;

      return (endTime = currentTime) =>
        endTime - breakStartTime < 60
          ? Math.floor(endTime - breakStartTime)
          : Math.floor((endTime - breakStartTime) % 60);
    },
    getUserError() {
      const error = Object.values(this.errors).filter((element) => {
        return element !== null;
      })[0];
      return () => (error instanceof UserError ? error : null);
    },
  },
  actions: {
    async startShift(
      areaId: number,
      shiftQuestionsResponses: Record<string, string> = {}
    ) {
      const analytics = useAnalyticsService();
      const rosterStore = useRosterStore();
      const userStore = useUserStore();

      const startedSource = rosterStore.getNextRosterForActiveUser()
        ? EventSources.StartShift
        : EventSources.SelectArea;

      const hasShiftQuestion = !!Object.keys(shiftQuestionsResponses).find(
        (key) => key !== 'commentBox'
      );

      analytics.track(
        { name: 'SHIFT_START_REQUESTED', screen: 'HOME' },
        {
          source: startedSource,
          shift: {
            details: this.getShiftActionAnalyticsPayload({
              areaID: areaId,
              hasShiftQuestion,
            }),
          },
        }
      );

      try {
        this.errors.startingShiftError = null;
        this.status.isStartingTimesheet = true;
        this.status.hasEndedBreak = false;
        const payload: Partial<EndShiftRequestPayload> = {};
        const { commentBox: employeeComment, ...customFieldValues } =
          shiftQuestionsResponses;

        if (employeeComment) {
          payload.strEmployeeComment = employeeComment;
        }
        if (Object.keys(customFieldValues).length) {
          payload.arrCustomFieldData = customFieldValues;
        }

        await startShift(areaId, userStore.data.kioskApiAuthorization, payload);

        analytics.track(
          { name: 'SHIFT_START_SUCCESS_ACKNOWLEDGED', screen: 'HOME' },
          {
            source: EventSources.StartShift,
            shift: {
              details: this.getShiftActionAnalyticsPayload({
                areaID: areaId,
                hasShiftQuestion,
              }),
            },
          }
        );

        await this.loadForActiveUser();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.startingShiftError = e;
          analytics.track(
            { name: 'SHIFT_START_FAILURE_ACKNOWLEDGED', screen: 'HOME' },
            {
              source: EventSources.StartShift,
              shift: {
                details: this.getShiftActionAnalyticsPayload({
                  areaID: areaId,
                  errorMessage: e.message,
                  hasShiftQuestion,
                }),
              },
            }
          );
        }
      } finally {
        this.status.isStartingTimesheet = false;
      }
    },

    async startBreak(
      timesheetId: number,
      breakType: string,
      breakId?: number,
      source?: string
    ) {
      const analytics = useAnalyticsService();
      const breakSource =
        source === 'selectBreakType'
          ? EventSources.SelectBreakType
          : EventSources.StartBreak;
      analytics.track(
        { name: 'SHIFT_BREAK_START_REQUESTED', screen: 'HOME' },
        {
          source: breakSource,
          shiftBreak: {
            details: this.getShiftActionAnalyticsPayload({
              breakType,
              timesheetId,
            }),
          },
        }
      );

      try {
        const userStore = useUserStore();
        this.errors.startingBreakError = null;
        this.status.isStartingBreak = true;
        this.status.hasEndedBreak = false;
        await pauseShift(
          timesheetId,
          userStore.data.kioskApiAuthorization,
          breakType,
          breakId
        );

        await this.loadForActiveUser();

        analytics.track(
          { name: 'SHIFT_BREAK_START_SUCCESS_ACKNOWLEDGED', screen: 'HOME' },
          {
            source: EventSources.StartBreak,
            shiftBreak: {
              details: this.getShiftActionAnalyticsPayload({
                breakType,
                timesheetId,
              }),
            },
          }
        );
      } catch (e) {
        if (e instanceof Error) {
          this.errors.startingBreakError = e;
          analytics.track(
            { name: 'SHIFT_BREAK_START_FAILURE_ACKNOWLEDGED', screen: 'HOME' },
            {
              source: EventSources.StartBreak,
              shiftBreak: {
                details: this.getShiftActionAnalyticsPayload({
                  breakType,
                  errorMessage: e.message,
                  timesheetId,
                }),
              },
            }
          );
        }
      } finally {
        this.status.isStartingBreak = false;
      }
    },

    async endBreak(timesheetId: number, breakType?: string, breakId?: number) {
      const analytics = useAnalyticsService();
      analytics.track(
        { name: 'SHIFT_BREAK_END_REQUESTED', screen: 'HOME' },
        {
          source: EventSources.EndBreak,
          shiftBreak: {
            details: this.getShiftActionAnalyticsPayload({
              breakType,
              timesheetId,
            }),
          },
        }
      );

      try {
        const userStore = useUserStore();
        this.errors.endingBreakError = null;
        this.status.isEndingBreak = true;
        await pauseShift(
          timesheetId,
          userStore.data.kioskApiAuthorization,
          breakType,
          breakId
        );

        await this.loadForActiveUser();

        const totalNumberOfBreaks: number =
          this.activeTimesheet?.Slots.length || 0;
        const endedBreakType =
          this.activeTimesheet?.Slots[totalNumberOfBreaks - 1].mixedActivity
            .strBreakType;

        analytics.track(
          { name: 'SHIFT_BREAK_END_SUCCESS_ACKNOWLEDGED', screen: 'HOME' },
          {
            source: EventSources.EndBreak,
            shiftBreak: {
              details: this.getShiftActionAnalyticsPayload({
                breakType: endedBreakType,
                timesheetId,
              }),
            },
          }
        );
      } catch (e) {
        if (e instanceof Error) {
          this.errors.endingBreakError = e;

          analytics.track(
            { name: 'SHIFT_BREAK_END_FAILURE_ACKNOWLEDGED', screen: 'HOME' },
            {
              source: EventSources.EndBreak,
              shiftBreak: {
                details: this.getShiftActionAnalyticsPayload({
                  breakType,
                  errorMessage: e.message,
                  timesheetId,
                }),
              },
            }
          );
        }
      } finally {
        this.status.isEndingBreak = false;
        this.status.hasEndedBreak = true;
      }
    },

    async endShift(
      timesheetId: number,
      shiftEndTime?: string,
      shiftStartTime?: string,
      shiftQuestionsResponses: Record<string, string> = {}
    ) {
      const analytics = useAnalyticsService();

      const hasShiftQuestion = !!Object.keys(shiftQuestionsResponses).find(
        (key) => key !== 'commentBox'
      );

      analytics.track(
        { name: 'SHIFT_END_REQUESTED', screen: 'HOME' },
        {
          source: EventSources.EndShiftConfirmation,
          shift: {
            details: this.getShiftActionAnalyticsPayload({
              timesheetId,
              hasShiftQuestion,
            }),
          },
        }
      );
      try {
        const userStore = useUserStore();
        this.errors.endingShiftError = null;
        this.status.isEndingTimesheet = true;
        const payload: Partial<EndShiftRequestPayload> = {};
        const { commentBox: employeeComment, ...customFieldValues } =
          shiftQuestionsResponses;

        if (shiftEndTime) {
          payload.strEndTime = shiftEndTime;
        }
        if (shiftStartTime) {
          payload.strStartTime = shiftStartTime;
        }
        if (employeeComment) {
          payload.strEmployeeComment = employeeComment;
        }
        if (Object.keys(customFieldValues).length) {
          payload.arrCustomFieldData = customFieldValues;
        }

        await endShift(
          timesheetId,
          userStore.data.kioskApiAuthorization,
          payload
        );

        analytics.track(
          { name: 'SHIFT_END_SUCCESS_ACKNOWLEDGED', screen: 'HOME' },
          {
            source: EventSources.EndShift,
            shift: {
              details: this.getShiftActionAnalyticsPayload({
                timesheetId,
                hasShiftQuestion,
              }),
            },
          }
        );

        await this.loadForActiveUser();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.endingShiftError = e;

          analytics.track(
            { name: 'SHIFT_END_FAILURE_ACKNOWLEDGED', screen: 'HOME' },
            {
              source: EventSources.EndShift,
              shift: {
                details: this.getShiftActionAnalyticsPayload({
                  errorMessage: e.message,
                  timesheetId,
                  hasShiftQuestion,
                }),
              },
            }
          );

          if (!this.canEndShift()) {
            await this.loadForActiveUser();
          } else {
            throw e;
          }
        }
      } finally {
        this.status.isEndingTimesheet = false;
      }
    },

    async updateShift(
      timesheetId?: number,
      shiftStartTime?: string,
      shiftEndTime?: string
    ) {
      const userStore = useUserStore();
      try {
        this.errors.editingShiftError = null;
        this.status.isEditingTimesheet = true;

        await postTimesheetAmend(
          {
            intTimesheetId: timesheetId,
            strStartTime: shiftStartTime,
            strEndTime: shiftEndTime,
          },
          userStore.data.kioskApiAuthorization
        );

        await this.loadForActiveUser();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.editingShiftError = e;
        }
      } finally {
        this.status.isEditingTimesheet = false;
      }
    },

    async updateShiftQuestions(
      timesheetId: number,
      areaId: number,
      shiftQuestionsResponses: Record<string, string> = {}
    ) {
      const userStore = useUserStore();
      const timesheetSetupStore = useTimesheetSetupStore();

      try {
        this.errors.editingShiftError = null;
        this.status.isEditingTimesheet = true;

        const { commentBox: employeeComment, ...customFieldValues } =
          shiftQuestionsResponses;

        await postTimesheetAmend(
          {
            intTimesheetId: timesheetId,
            arrCustomFieldData: customFieldValues,
            strEmployeeComment: employeeComment,
          },
          userStore.data.kioskApiAuthorization
        );

        await this.loadForActiveUser();
        await timesheetSetupStore.load(DisplayTiming.ClockOutAndUpdate, areaId);
      } catch (e) {
        if (e instanceof Error) {
          this.errors.editingShiftError = e;
        }
      } finally {
        this.status.isEditingTimesheet = false;
      }
    },

    async updateBreak(timesheetId?: number, breaksSlot?: any) {
      const userStore = useUserStore();
      try {
        this.errors.editingShiftError = null;
        this.status.isEditingTimesheet = true;

        await postTimesheetAmend(
          {
            intTimesheetId: timesheetId,
            arrSlots: breaksSlot,
          },
          userStore.data.kioskApiAuthorization
        );

        await this.loadForActiveUser();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.editingShiftError = e;
        }
      } finally {
        this.status.isEditingTimesheet = false;
      }
    },

    async updateArea(timesheetId?: number, areaId?: number) {
      const userStore = useUserStore();
      try {
        this.errors.editingShiftError = null;
        this.status.isEditingTimesheet = true;

        await postTimesheetAmend(
          {
            intTimesheetId: timesheetId,
            intOpunitId: areaId,
          },
          userStore.data.kioskApiAuthorization
        );

        await this.loadForActiveUser();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.editingShiftError = e;
        }
      } finally {
        this.status.isEditingTimesheet = false;
      }
    },

    getShiftActionAnalyticsPayload({
      areaID,
      breakType,
      errorMessage,
      timesheetId,
      hasShiftQuestion,
    }: ShiftActionTrackingParams = {}) {
      const payload: ShiftActionTrackingParams = {};
      const areas = useAreaStore();
      const userStore = useUserStore();
      const rosterStore = useRosterStore();

      if (areaID) {
        const area = areas.getAreaById(areaID);
        payload.areaID = areaID;
        payload.locationID = area?.Company;
      }

      payload.isScheduledShift = false;

      if (userStore.isAuthenticated()) {
        const employeeWithTimesheetsStore = useEmployeeWithTimesheetsStore();
        const activeUser = userStore.activeUserOrThrow.Id;
        try {
          payload.isScheduledShift =
            employeeWithTimesheetsStore.isScheduled(activeUser);

          const nextRoster = rosterStore.getNextRosterForActiveUser();
          if (nextRoster) {
            const area = areas.getAreaById(nextRoster?.OperationalUnit);
            payload.areaID = nextRoster?.OperationalUnit;
            payload.locationID = area?.Company;
            payload.rosterId = nextRoster.Id;
          }
        } catch (e) {
          payload.isScheduledShift = false;
        }
      }

      if (timesheetId) {
        const timesheet = this.getActiveUserTimesheetById(timesheetId);
        if (timesheet?.Roster) {
          payload.rosterId = timesheet.Roster;
          payload.isScheduledShift = true;
        }

        if (timesheet?.OperationalUnit) {
          const area = areas.getAreaById(timesheet?.OperationalUnit);
          payload.areaID = timesheet?.OperationalUnit;
          payload.locationID = area?.Company;
        }
        if (this.activeTimesheet) {
          const activeBreak = this.getActiveBreak();
          if (activeBreak && activeBreak.mixedActivity.strBreakType) {
            payload.breakType = mapBreakTypes(
              activeBreak.mixedActivity.strBreakType
            );
          }
        }
      }

      if (breakType) {
        payload.breakType = mapBreakTypes(breakType);
      }

      if (errorMessage) {
        payload.errorMessage = errorMessage;
      }

      if (hasShiftQuestion !== undefined) {
        payload.hasShiftQuestion = hasShiftQuestion;
      }

      return payload;
    },

    async load() {
      this.errors.loadingError = null;
      this.errors.endingShiftError = null;
      this.errors.startingShiftError = null;
      this.errors.startingBreakError = null;
      this.errors.endingBreakError = null;
      this.errors.editingShiftError = null;

      this.status.isLoading = true;
      try {
        this.data = toTimesheetsEmployeeMap(await fetchTimesheets());
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },

    async loadForActiveUser() {
      const userStore = useUserStore();
      this.errors.loadingError = null;
      this.errors.endingShiftError = null;
      this.errors.startingShiftError = null;
      this.errors.startingBreakError = null;
      this.errors.endingBreakError = null;
      this.errors.editingShiftError = null;

      this.status.isLoading = true;
      try {
        this.data[userStore.activeUserOrThrow.Id] = await fetchUserTimesheets(
          userStore.data.kioskApiAuthorization
        );
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },

    setEndingShiftError(timesheetErrorStatus: TimesheetErrorResponseStatus) {
      this.errors.endingShiftError = new UserError(
        new Error(TimesheetErrorResponseMessageMap[timesheetErrorStatus]),
        timesheetErrorStatus
      );
    },
  },
});
